import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import MobileNavigation from '../Navigation/MobileNavigation';
import Footer from '../Footer/Footer';
import aboutUsEn from '../../assets/about-us.webp';
import aboutUsEs from '../../assets/sobre-nosotros.webp';

const AboutUsPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aboutUsImage = i18n.language.startsWith('es') ? aboutUsEs : aboutUsEn;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden selection:bg-[#05a5b7] selection:text-white flex flex-col">
      <SEOHead title={t('aboutUs.title')} />
      <DesktopNavigation />
      <MobileNavigation />
      
      <main className="flex-grow pt-24 xl:pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-8 max-w-6xl">
          
          {/* Top Section: Image and Mission/Vision */}
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left Column: Image and Verse Banner */}
            <div className="w-full lg:w-5/12 flex flex-col rounded-lg overflow-hidden">
              <img 
                src={aboutUsImage} 
                alt="About Us" 
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Right Column: Mission and Vision */}
            <div className="w-full lg:w-7/12 flex flex-col justify-center">
              <div className="mb-10">
                <h2 className="text-3xl font-bold mb-4 font-oswald tracking-wide">{t('aboutUs.mission.title')}</h2>
                <div className="text-gray-700 space-y-4 text-base leading-relaxed">
                  <p>{t('aboutUs.mission.p1')}</p>
                  <p>{t('aboutUs.mission.p2')}</p>
                  <p>{t('aboutUs.mission.p3')}</p>
                  <p>{t('aboutUs.mission.p4')}</p>
                </div>
              </div>
{/*               
              <div>
                <h2 className="text-3xl font-bold mb-4 font-oswald tracking-wide">{t('aboutUs.vision.title')}</h2>
                <div className="text-gray-700 space-y-4 text-base leading-relaxed">
                  <p>
                    <Trans i18nKey="aboutUs.vision.p1">
                      <strong>Christ Reigns Media exists to see Christ reign in the media.</strong>
                    </Trans>
                  </p>
                  <p>
                    <Trans i18nKey="aboutUs.vision.p2">
                      We envision becoming a leading digital ministry where millions worldwide turn daily for biblical truth, encouragement, and a deeper walk with Christ. At the same time, we seek to be a <strong>catalyst that inspires countless others to rise up and flood today's media with the Gospel</strong>—until the <strong>light of Christ shines brighter than the darkness of this world.</strong> The very name <em>Christ Reigns Media</em> declares this vision.
                    </Trans>
                  </p>
                </div>
              </div> */}
            </div>
          </div>

          <hr className="border-gray-200 my-5" />

          {/* Who We Are */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 font-oswald tracking-wide">{t('aboutUs.whoWeAre')}</h2>
            
            {/* Maria Luisa */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold mb-6 tracking-wide border-b border-gray-100 pb-2">{t('aboutUs.mariaLuisa.name')}</h3>
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start">
                {/* Video Column */}
                <div className="w-full lg:w-1/2">
                  <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-gray-50">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/8glpubYk_8s"
                      title={`${t('aboutUs.mariaLuisa.name')} - Video`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
                {/* Content Column */}
                <div className="w-full lg:w-1/2 text-gray-700 space-y-4 text-base leading-relaxed">
                  <p>{t('aboutUs.mariaLuisa.p1')}</p>
                  <p dangerouslySetInnerHTML={{ __html: t('aboutUs.mariaLuisa.p2') }} />
                  <p dangerouslySetInnerHTML={{ __html: t('aboutUs.mariaLuisa.p3') }} />
                </div>
              </div>
            </div>

            {/* Alan Jones */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold mb-6 tracking-wide border-b border-gray-100 pb-2">{t('aboutUs.alanJones.name')}</h3>
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start">
                {/* Video Column */}
                <div className="w-full lg:w-1/2">
                  <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-gray-50">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/Li3shhu8IB8"
                      title={`${t('aboutUs.alanJones.name')} - Video`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
                {/* Content Column */}
                <div className="w-full lg:w-1/2 text-gray-700 space-y-4 text-base leading-relaxed">
                  <p dangerouslySetInnerHTML={{ __html: t('aboutUs.alanJones.p1') }} />
                  <p dangerouslySetInnerHTML={{ __html: t('aboutUs.alanJones.p2') }} />
                </div>
              </div>
            </div>
          </div>

          {/* Why We Do This */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 font-oswald tracking-wide">{t('aboutUs.whyWeDoThis.title')}</h2>
            <div className="text-gray-700 space-y-4 text-base leading-relaxed">
              <p dangerouslySetInnerHTML={{ __html: t('aboutUs.whyWeDoThis.p1') }} />
              <blockquote className="pl-6 py-2 my-6 font-semibold italic text-gray-800 bg-gray-50 rounded-r-lg border-l-4 border-gray-300">
                {t('aboutUs.whyWeDoThis.quote')}
              </blockquote>
              <p>{t('aboutUs.whyWeDoThis.p2')}</p>
            </div>
          </div>

          {/* Join Us */}
          <div className="mb-10">
            <h2 className="text-3xl font-bold mb-4 font-oswald tracking-wide">{t('aboutUs.joinUs.title')}</h2>
            <p className="text-gray-700 text-base leading-relaxed">
              {t('aboutUs.joinUs.text')}
            </p>
          </div>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutUsPage;
